/**
 * File: TextPrimitive.tsx
 * Path: apps/admin/src/shared/components/TextPrimitive.tsx
 * Description: The text building blocks every admin page uses, so type looks
 * the same everywhere (Liam approved the style 2026-10-18): card title with a
 * gold bar, card description, field label and value, amounts with a progress
 * bar, numbered sections with points, the "not included" box, codes and
 * status chips. Pages use these instead of their own text classes.
 */

import React from "react";
import { Check } from "lucide-react";
import { cn } from "@idk/utils";

export const titleTextClass =
  "border-l-[3px] border-primary pl-stack-sm text-[17px] font-extrabold leading-tight tracking-normal normal-case text-foreground";

/** 17px / 800 title, foreground, with a 3px gold bar before it. */
export const TitleText: React.FC<{
  id?: string;
  as?: "h2" | "h3";
  className?: string;
  children: React.ReactNode;
}> = ({ id, as: Tag = "h2", className, children }) => (
  <Tag id={id} className={cn(titleTextClass, className)}>
    {children}
  </Tag>
);

/** 14px muted line under a title. */
export const DescriptionText: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => (
  <p className={cn("text-sm text-muted-foreground", className)}>{children}</p>
);

/** 12px / 700 capitals, faint: the label above a value. */
export const labelTextClass = "text-xs font-bold uppercase tracking-wider text-muted-foreground";

/** The value under a label when the page builds its own list: 16px / 600. */
export const valueTextClass = "break-words text-base font-semibold";

/** A label with its value: 16px / 600, second lines 14px muted. */
export const FieldValue: React.FC<{
  label: string;
  children: React.ReactNode;
  /** Second lines (a contact name, a hint), each shown 14px muted. */
  lines?: ReadonlyArray<string | undefined | null>;
  className?: string;
}> = ({ label, children, lines = [], className }) => (
  <div className={cn("min-w-0 space-y-stack-xs", className)}>
    <dt className={labelTextClass}>{label}</dt>
    <dd className="break-words text-base font-semibold">{children}</dd>
    {lines.filter(Boolean).map((line) => (
      <dd key={line} className="break-words text-sm text-muted-foreground">
        {line}
      </dd>
    ))}
  </div>
);

/** A bordered grid of FieldValues, two across from a tablet up. */
export const FieldGrid: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <dl className={cn("grid gap-px border border-border/50 bg-border/50 sm:grid-cols-2 [&>div]:bg-card [&>div]:p-gutter-sm", className)}>
    {children}
  </dl>
);

/** Bold tabular figures; a key total is 20 to 26px / 800. */
export const AmountText: React.FC<{ children: React.ReactNode; size?: "normal" | "key" | "big"; className?: string }> = ({
  children,
  size = "normal",
  className,
}) => (
  <span
    className={cn(
      "tabular-nums",
      size === "normal" && "font-bold",
      size === "key" && "text-xl font-extrabold",
      size === "big" && "text-[26px] font-extrabold leading-none",
      className,
    )}
  >
    {children}
  </span>
);

/** Where something part-paid stands. `percent` is 0 to 100. */
export const ProgressBar: React.FC<{ percent: number; label: string }> = ({ percent, label }) => (
  <div
    role="progressbar"
    aria-label={label}
    aria-valuemin={0}
    aria-valuemax={100}
    aria-valuenow={Math.max(0, Math.min(100, Math.round(percent)))}
    className="h-1.5 w-full bg-secondary"
  >
    <div className="h-full bg-tone-green" style={{ width: `${Math.max(0, Math.min(100, percent))}%` }} />
  </div>
);

/** A numbered heading (gold number) with its points beneath it. */
export const NumberedSection: React.FC<{ number: number; title: string; children?: React.ReactNode }> = ({
  number,
  title,
  children,
}) => (
  <section className="space-y-stack-sm border-t border-border/50 py-gutter-sm first:border-t-0">
    <h3 className="flex gap-stack-sm text-base font-bold">
      <span className="w-5 shrink-0 text-primary tabular-nums">{number}</span>
      <span>{title}</span>
    </h3>
    {children}
  </section>
);

/** The points under a numbered heading. A point may start with a bold "Label:". */
export const PointList: React.FC<{ points: ReadonlyArray<{ label?: string; text: string }> }> = ({ points }) => (
  <ul className="space-y-stack-xs pl-7 text-[14.5px] leading-relaxed">
    {points.map((point, index) => (
      <li key={`${index}-${point.text}`} className="flex max-w-[78ch] gap-stack-sm">
        <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 bg-muted-foreground/60" />
        <span>
          {point.label && <strong className="font-bold">{point.label} </strong>}
          {point.text}
        </span>
      </li>
    ))}
  </ul>
);

/** A quiet box with a rule down its side: what is not covered, or a side note. */
export const NoteBox: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <aside className="space-y-stack-xs border-l-[3px] border-border bg-secondary/40 p-gutter-sm">
    <p className={labelTextClass}>{title}</p>
    <p className="max-w-[78ch] text-[14.5px] leading-relaxed">{children}</p>
  </aside>
);

/** A code or reference: mono 13px in a thin box. */
export const CodeText: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <span className={cn("inline-block border border-border/60 px-stack-xs py-0.5 font-mono text-[13px]", className)}>{children}</span>
);

export type ChipTone = "green" | "red" | "amber" | "blue" | "neutral";

const CHIP_TONE: Record<ChipTone, string> = {
  green: "bg-tone-green/15 text-tone-green",
  red: "bg-tone-red/10 text-tone-red",
  amber: "bg-tone-amber/10 text-tone-amber",
  blue: "bg-tone-blue/10 text-tone-blue",
  neutral: "bg-secondary text-muted-foreground",
};

/** A status as a tone chip. */
export const StatusChip: React.FC<{ tone: ChipTone; children: React.ReactNode }> = ({ tone, children }) => (
  <span className={cn("inline-flex w-fit items-center whitespace-nowrap rounded-none px-stack-sm py-stack-xs text-xs font-bold uppercase tracking-wider", CHIP_TONE[tone])}>
    {children}
  </span>
);

/** "✓ Received" in green, then the date. */
export const ReceivedText: React.FC<{ date?: string; word?: string; children?: React.ReactNode }> = ({ date, word = "Received", children }) => (
  <span className="inline-flex flex-wrap items-center gap-x-stack-xs text-sm">
    <span className="inline-flex items-center gap-1 font-bold text-tone-green">
      <Check className="h-4 w-4" aria-hidden />
      {word}
    </span>
    {date && <span>{date}</span>}
    {children}
  </span>
);
